import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import DropZone from "../components/DropZone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function PdfWatermark() {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [text, setText] = useState("CONFIDENCIAL");
  const [opacity, setOpacity] = useState(0.3);
  const [size, setSize] = useState(60);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    if (!file || !text.trim()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("text", text);
      fd.append("opacity", opacity);
      fd.append("font_size", size);
      const { data } = await api.post("/pdf/watermark", fd);
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-12" data-testid="pdf-watermark-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_watermark")}</h1>
      <p className="text-zinc-500 mt-2">{t("tool_watermark_desc")}</p>

      <div className="mt-8">
        {!file ? (
          <DropZone accept="application/pdf" onFiles={(fs) => setFile(fs[0])} testId="watermark-dropzone" />
        ) : (
          <div className="border border-zinc-200 rounded-md p-6 bg-white space-y-5">
            <div className="hero-h2 text-xl">{file.name}</div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-3">
                <Label className="eyebrow">{t("watermark_text")}</Label>
                <Input value={text} onChange={(e) => setText(e.target.value)}
                  className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="watermark-text" />
              </div>
              <div>
                <Label className="eyebrow">{t("watermark_opacity")} (0–1)</Label>
                <Input type="number" step="0.05" min="0" max="1" value={opacity}
                  onChange={(e) => setOpacity(parseFloat(e.target.value))}
                  className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="watermark-opacity" />
              </div>
              <div>
                <Label className="eyebrow">{t("pdf_font_size")}</Label>
                <Input type="number" value={size} onChange={(e) => setSize(parseInt(e.target.value) || 60)}
                  className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="watermark-size" />
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={run} disabled={busy}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11" data-testid="watermark-run">
                {busy ? t("processing") : `${t("cta_apply")} →`}
              </Button>
              <Button variant="ghost" className="rounded-sm h-11" onClick={() => { setFile(null); setResult(null); }}>
                {t("cta_clear")}
              </Button>
            </div>
          </div>
        )}
        {result && (
          <div className="mt-6 border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="watermark-result">
            <span className="eyebrow">{t("result_ready")}</span>
            <div className="hero-h2 text-xl mt-1">{result.original_filename}</div>
            <Button onClick={() => downloadFile(result.id, result.original_filename)}
              className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10 mt-4">
              ↓ {t("cta_download")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
